import React from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import EmailEditor from '../components/email-editor-view';
import {hasIllegalCharacters} from 'core/input-validations';
import {organizationDomains} from 'modules/organization-module';
import {emailVerificationRequestPost} from 'modules/identity-module';
import {uiShowToastrMessage} from 'modules/ui-module';
import {
  meData,
  meIsLoading,
  meIsOrganizationMember,
  meIsEmailChangeAllowed,
  meLocale,
  meRequestPatch,
  meForceSamlSsoLogin,
  meIsConflicted,
} from 'modules/me-module';

export class EmailEditorContainer extends React.Component {
  constructor(props, context) {
    super(props, context);
    this.state = {
      personData: Object.assign({}, this.props.personData),
      errors: {},
      isDirty: false,
      modalIsOpen: false,
      toastrType: '',
      toastrMessageId: '',
      domainValue: '',
      domainUserName: '',
      recoveryEmail: this.props.recoveryEmail || '',
      domainOptions: [],
      modifiedUserName: '',
      isRecoveryEmailModified: false,
      emailEditorClass: '',
      verifyEmailClass: 'hidden',
    };
  }

  componentDidMount() {
    if (
      this.props.isOrganizationMember &&
      this.props.userDomain.length &&
      this.props.domainUserName.length
    ) {
      this.setState({domainValue: this.props.userDomain, domainUserName: this.props.domainUserName});
    }
  }

  componentDidUpdate = (prevProps) => {
    const { isOrganizationMember, userDomain, domainUserName } = this.props;
    if (
      isOrganizationMember &&
      (userDomain !== prevProps.userDomain) ||
      (domainUserName !== prevProps.domainUserName)
    ) {
      this.setState({domainValue: userDomain, domainUserName});
    }
  };

  exceedsMaxEmailLength = (field, value) => {
    let emailAddress = value;
    if (field.indexOf('domainUserName') > -1) {
      emailAddress = `${value}@${this.state.domainValue}`;
    }
    return emailAddress.length > 128;
  };

  validateUserInput = (activeField, value, onBlurCheck) => {
    let errorText = '';
    const field = activeField === 'domainSelection' ? 'domainUserName' : activeField;
    const errors = {...this.state.errors};
    if (errors[field]) {
      delete errors[field];
      this.setState({errors});
    }

    if (hasIllegalCharacters(value)) {
      errorText = 'shared.illegal-character-entry';
    }

    if (
      !errorText.length &&
      field.indexOf('domainUserName') > -1 &&
      activeField !== 'domainSelection' &&
      typeof this.props.domainOptions !== 'undefined' &&
      this.props.domainOptions.length &&
      value.indexOf('@') > -1
    ) {
      errorText = 'email-editor.validation-error-domain-entered';
    }

    if (
      !errorText.length &&
      onBlurCheck &&
      value.length &&
      (field === 'recoveryEmail' || field === 'userName') &&
      !value.match(/^([\w.%+-]+)@([\w-]+\.)+([\w]{2,})$/i)
    ) {
      errorText = 'email-editor.validation-error-email';
    }

    if (!errorText.length && this.exceedsMaxEmailLength(activeField, value)) {
      errorText = 'email-editor.validation-error-email-length-exceeded';
    }

    if (errorText.length) {
      errors[field] = errorText;
      this.setState({errors});
      return false;
    }
    return true;
  };

  openModal = (e) => {
    if (typeof e !== 'undefined') {
      e.stopPropagation();
      e.preventDefault();
    }
    this.origState = {...this.state};
    this.setState({modalIsOpen: true});
  };

  closeModal = (e) => {
    if (typeof e !== 'undefined') {
      e.stopPropagation();
      e.preventDefault();
    }
    this.setState(this.origState);
  };

  completeProcess = (e) => {
    if (typeof e !== 'undefined') {
      e.stopPropagation();
      e.preventDefault();
    }
    this.showEmailEditor();
    this.setState({modalIsOpen: false});
  };

  displayToastrMessage = (toastrType, toastrMessageId) => {
    this.props.uiShowToastrMessage(toastrType, toastrMessageId);
  };

  onBlur = (e) => {
    const field = (e.target.name || e.target.id).split('-')[0];
    const value = e.target.value;
    this.validateUserInput(field, value, true);
    return true;
  };

  onChange = (e) => {
    let field = (e.target.name || e.target.id).split('-')[0];
    let value = e.target.value;

    const stateUpdates = {};
    stateUpdates.isDirty = true;

    const personData = {...this.state.personData};

    if (field.indexOf('domainUserName') > -1) {
      if (this.validateUserInput(field, value, false)) {
        stateUpdates.domainUserName = value;
        field = 'userName';
        value = `${value}@${this.state.domainValue}`;
      } else {
        return false;
      }
    }

    if (field !== 'recoveryEmail') {
      personData[field] = value;
    }

    if (this.validateUserInput(field, value, false)) {
      if (field !== 'recoveryEmail') {
        stateUpdates.personData = personData;
        stateUpdates.modifiedUserName = value;
      } else {
        stateUpdates.isRecoveryEmailModified = true;
        stateUpdates.recoveryEmail = value;
      }
    }

    this.setState({...stateUpdates});

    return true;
  };

  onDomainChange = (selection) => {
    const stateChanges = {isDirty: true};
    const personData = {...this.state.personData};
    // The domain selector control returns the newly selected value and label in an object, not the control itself
    // so we must handle the returned values accordingly.
    if (selection.value && selection.label) {
      const completeUserName = `${this.state.domainUserName}@${selection.value}`;
      if (this.validateUserInput('domainSelection', completeUserName, false)) {
        personData['userName'] = completeUserName;
        stateChanges.personData = personData;
        stateChanges.domainValue = selection.value;
        stateChanges.modifiedUserName = completeUserName;
      }
    }
    this.setState(stateChanges);
    return true;
  };

  sendEmailVerificationRequest = () => this.props.emailVerificationRequestPost(this.props.locale, {email: this.state.modifiedUserName});

  handlePrimaryEmailVerification = (e, showSuccessToastrMessage = true) => {
    let showVerificationStep = false;
    if (typeof e !== 'undefined') {
      e.stopPropagation();
      e.preventDefault();
    } else {
      showVerificationStep = true;
    }

    this.sendEmailVerificationRequest()
      .then(
        () => {
          if (showSuccessToastrMessage) {
            this.displayToastrMessage('success', 'email-editor.verification-email-send-successful');
          }
          if (showVerificationStep) { this.toggleEmailEditor(true); }
        },
        () => {
          this.displayToastrMessage('error', 'email-editor.verification-email-send-failed');
        },
      );
  };

  getRecoveryEmailPatchContent = () => {
    let patchContent = '';
    if (this.state.recoveryEmail.length) {
      patchContent = {
        emails: [{
          value: this.state.recoveryEmail,
          type: 'recovery',
          primary: false,
        }]};
    } else {
      patchContent = {
        meta: { attributes: ['emails'] },
      };
    }
    return patchContent;
  };

  onSave = () => {
    if (this.state.isRecoveryEmailModified) {
      return this.props.meRequestPatch(this.getRecoveryEmailPatchContent())
        .then(
          () => {
            this.displayToastrMessage('success', 'email-editor.recovery-email-save-successful');
            if (this.state.modifiedUserName.length) {
              this.handlePrimaryEmailVerification();
            } else {
              this.setState({modalIsOpen: false});
            }
          },
          () => {
            this.displayToastrMessage('error', 'email-editor.recovery-email-save-failed');
          },
        );
    } else if (!this.state.isRecoveryEmailModified && this.state.modifiedUserName.length) {
      return this.handlePrimaryEmailVerification();
    }
    return true;
  };

  enableSubmit = () => (
    Object.keys(this.state.errors).length === 0 &&
    this.state.isDirty &&
    !this.props.isLoading &&
    !this.props.isViewOnly &&
    this.state.personData.userName.length > 0 &&
    this.state.personData.userName.charAt(0) !== '@'
  );

  showEmailEditor = (e) => {
    if (typeof e !== 'undefined') {
      e.stopPropagation();
      e.preventDefault();
    }
    this.toggleEmailEditor(false);
    return true;
  };

  toggleEmailEditor = (showEmailEditor) => {
    const emailEditorClass = showEmailEditor ? 'hidden' : '';
    const verifyEmailClass = showEmailEditor ? '' : 'hidden';
    this.setState({emailEditorClass, verifyEmailClass});
  };

  render() {
    return (
      <EmailEditor
        personData={this.state.personData}
        isEmailChangeAllowed={this.props.isEmailChangeAllowed}
        onEditLinkClick={this.openModal}
        modalIsOpen={this.state.modalIsOpen}
        closeModal={this.closeModal}
        completeProcess={this.completeProcess}
        onBlur={this.onBlur}
        onChange={this.onChange}
        onSave={this.onSave}
        enableSubmit={this.enableSubmit()}
        isLoading={this.props.isLoading}
        errors={this.state.errors}
        domainValue={this.state.domainValue}
        domainUserName={this.state.domainUserName}
        recoveryEmail={this.state.recoveryEmail}
        domainOptions={this.props.domainOptions}
        onDomainChange={this.onDomainChange}
        showEmailEditor={this.showEmailEditor}
        modifiedUserName={this.state.modifiedUserName}
        handlePrimaryEmailVerification={this.handlePrimaryEmailVerification}
        emailEditorClass={this.state.emailEditorClass}
        verifyEmailClass={this.state.verifyEmailClass}
        forceSamlSsoLogin={this.props.forceSamlSsoLogin}
        forceSamlSsoClass={this.props.forceSamlSsoLogin ? 'hidden' : ''}
        isViewOnly={this.props.isViewOnly}
      />
    );
  }
}

EmailEditorContainer.propTypes = {
  personData: PropTypes.object.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isOrganizationMember: PropTypes.bool.isRequired,
  domainOptions: PropTypes.array,
  userDomain: PropTypes.string,
  domainUserName: PropTypes.string,
  recoveryEmail: PropTypes.string,
  locale: PropTypes.string.isRequired,
  emailVerificationRequestPost: PropTypes.func.isRequired,
  meRequestPatch: PropTypes.func.isRequired,
  isEmailChangeAllowed: PropTypes.bool.isRequired,
  uiShowToastrMessage: PropTypes.func.isRequired,
  forceSamlSsoLogin: PropTypes.bool.isRequired,
  isViewOnly: PropTypes.bool,
};

export const mapStateToProps = (state) => {
  const personData = meData(state);
  const isOrganizationMember = meIsOrganizationMember(state);
  const domainOptions = [];
  const orgDomains = organizationDomains(state);
  let domainUserName = '';
  let userDomain = '';
  if (isOrganizationMember && orgDomains.length) {
    orgDomains.forEach((domain) => {
      if (!domain.token) {
        domainOptions.push({value: domain.name, label: `@${domain.name}`});
      }
    });
    // during unit testing personData may not have a 'userName' property, so this check is necessary.
    const userNameParts = personData.userName ? personData.userName.split('@') : ['', ''];
    domainUserName = userNameParts[0];
    userDomain = userNameParts[1];
  }
  const emails = personData.emails || [];
  const recoveryEmailObj = emails.find((email) => (email.type === 'recovery' && !email.primary));
  const recoveryEmail = recoveryEmailObj ? recoveryEmailObj.value : '';

  return {
    personData,
    isLoading: meIsLoading(state),
    isOrganizationMember,
    domainUserName,
    recoveryEmail,
    userDomain,
    domainOptions,
    locale: meLocale(state),
    isEmailChangeAllowed: meIsEmailChangeAllowed(state),
    forceSamlSsoLogin: meForceSamlSsoLogin(state),
    isViewOnly: meIsConflicted(state),
  };
};

export const mapDispatchToProps = (dispatch) => ({
  emailVerificationRequestPost: (locale, payload) => dispatch(emailVerificationRequestPost(locale, payload)),
  meRequestPatch: (payload) => dispatch(meRequestPatch(payload)),
  uiShowToastrMessage: (toastrType, toastrMessageId) => dispatch(uiShowToastrMessage(toastrType, toastrMessageId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(EmailEditorContainer);
